/**
 * @fileoverview Admin login page. Password verification runs on Supabase (Edge Function);
 * the bcrypt hash is not shipped to the browser.
 */

import React, { useState, useRef } from "react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import useAdminAuth from "../store/useAdminAuth";
import { verifyAdminPassword } from "../api/adminapi";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { useNavigate, useLocation } from "react-router-dom";

const Login = () => {
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  /** Stops duplicate submits before React re-renders (honest double-clicks); not a security boundary. */
  const submitLockRef = useRef(false);
  const { login } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || "/admin";

  /**
   * Sends the password to the Edge Function for bcrypt verification; never compares hashes on the client.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitLockRef.current || isSubmitting) return;
    submitLockRef.current = true;
    setMessage("");
    setIsSubmitting(true);
    try {
      const result = await verifyAdminPassword(password);
      if (result.ok) {
        login();
        navigate(from, { replace: true });
      } else if (result.error) {
        setMessage("Unable to verify password. Please try again.");
      } else {
        setMessage("Incorrect password");
      }
    } finally {
      submitLockRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full h-screen flex justify-center items-center">
      <Card className="h-[300px] w-[400px] p-4">
        <CardHeader className="pb-5">
          <CardTitle>Admin Login</CardTitle>
          <CardDescription>Enter the admin password to access</CardDescription>
          <p className="text-sm text-red-400">{message}</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <div className="grid w-full items-center gap-4">
              <div className="flex flex-col space-y-1.5">
                <Label>Password</Label>
                <Input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div className="flex flex-col space-y-1.5">
                <Button
                  type="submit"
                  disabled={isSubmitting}>
                  {isSubmitting ? "Verifying…" : "Login"}
                </Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
